import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "./logo-black.png.webp";
import TechBackground from "./TechBackground";
import { publicRegister } from "../redux/actionCreator";
import "./login.scss";

/**
 * Public self-service registration. Anyone can hit /register and fill the
 * form; the backend creates the user with status="pending" and an admin
 * approves them under Audit & Access → Users before they can log in.
 *
 * Validates the same constraints as the backend so the user gets fast
 * feedback before submit (the server still re-validates).
 */
const Register = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    password: "",
    confirm: "",
  });
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => {
    setData((s) => ({ ...s, [k]: v }));
    setErr((e) => ({ ...e, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!data.firstname.trim() || data.firstname.trim().length < 2) {
      e.firstname = "First name (2+ chars) required";
    }
    if (data.lastname && data.lastname.length > 50) {
      e.lastname = "Too long";
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email.trim())) {
      e.email = "Enter a valid email";
    }
    if (data.password.length < 8) {
      e.password = "At least 8 characters";
    } else if (!/[A-Za-z]/.test(data.password) || !/[0-9]/.test(data.password)) {
      e.password = "Must include a letter and a number";
    }
    if (data.confirm !== data.password) {
      e.confirm = "Passwords don't match";
    }
    if (data.phone && !/^[+0-9 .()\-]{6,32}$/.test(data.phone)) {
      e.phone = "Looks invalid";
    }
    setErr(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!validate() || busy) return;
    setBusy(true);
    const res = await publicRegister({
      firstname: data.firstname.trim(),
      lastname: data.lastname.trim() || undefined,
      email: data.email.trim().toLowerCase(),
      phone: data.phone.trim() || undefined,
      password: data.password,
    });
    setBusy(false);
    if (res?.status) {
      setDone(true);
      toast.success(res.message || "Registration submitted");
    } else {
      toast.error(res?.message || "Registration failed");
    }
  };

  return (
    <div className="security__login tech-theme">
      <TechBackground />
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 pe-0">
            <div className="login__section">
              <div className="logo-wrap">
                <span className="logo-ring logo-ring--1" />
                <span className="logo-ring logo-ring--2" />
                <span className="logo-orbit">
                  <span className="logo-orbit__dot" />
                </span>
                <img src={logo} alt="Smart Hatch Technologies" className="logo" />
              </div>
              <br />
              {done ? (
                <>
                  <h2>Thanks — you're on the list.</h2>
                  <p style={{ color: "#94a3b8", marginTop: 10 }}>
                    An admin will review your request and reach out at{" "}
                    <strong>{data.email}</strong>. Once approved, you can sign
                    in with the password you just set.
                  </p>
                  <button
                    className="btn-primary"
                    onClick={() => navigate("/")}
                    style={{ marginTop: 16 }}
                  >
                    Back to sign in
                  </button>
                </>
              ) : (
                <>
                  <h2>Request access</h2>
                  <p style={{ color: "#94a3b8", margin: "0 0 16px" }}>
                    Fill in your details — admins will review and approve.
                  </p>
                  <form onSubmit={submit} noValidate>
                    <div className="row">
                      <div className="col-6">
                        <div className="form-group">
                          <label>First name *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={data.firstname}
                            onChange={(e) => set("firstname", e.target.value)}
                            autoComplete="given-name"
                            disabled={busy}
                          />
                          {err.firstname && (
                            <small className="text-danger">{err.firstname}</small>
                          )}
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="form-group">
                          <label>Last name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={data.lastname}
                            onChange={(e) => set("lastname", e.target.value)}
                            autoComplete="family-name"
                            disabled={busy}
                          />
                          {err.lastname && (
                            <small className="text-danger">{err.lastname}</small>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        className="form-control"
                        value={data.email}
                        onChange={(e) => set("email", e.target.value)}
                        autoComplete="email"
                        disabled={busy}
                      />
                      {err.email && (
                        <small className="text-danger">{err.email}</small>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={data.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        autoComplete="tel"
                        disabled={busy}
                      />
                      {err.phone && (
                        <small className="text-danger">{err.phone}</small>
                      )}
                    </div>
                    <div className="row">
                      <div className="col-6">
                        <div className="form-group">
                          <label>Password *</label>
                          <input
                            type="password"
                            className="form-control"
                            value={data.password}
                            onChange={(e) => set("password", e.target.value)}
                            autoComplete="new-password"
                            disabled={busy}
                          />
                          {err.password && (
                            <small className="text-danger">{err.password}</small>
                          )}
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="form-group">
                          <label>Confirm *</label>
                          <input
                            type="password"
                            className="form-control"
                            value={data.confirm}
                            onChange={(e) => set("confirm", e.target.value)}
                            autoComplete="new-password"
                            disabled={busy}
                          />
                          {err.confirm && (
                            <small className="text-danger">{err.confirm}</small>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={busy}
                      style={{ marginTop: 8 }}
                    >
                      {busy ? "Submitting…" : "Submit for approval"}
                    </button>
                  </form>
                  <Link to="/" className="admin_login mt-3">
                    Already have an account? Sign in
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
        <p className="m-0 copyright">© SMART HATCH TECHNOLOGIES FZE</p>
      </div>
    </div>
  );
};

export default Register;
